import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Bell, ListChecks,
  Loader2, Trash2, Clock, ExternalLink,
} from "lucide-react";

const WEEKDAYS = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];
const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const EVENT_TIPOS = [
  { value: "reunion", label: "Reunión", color: "#7A1E2E" },
  { value: "recordatorio", label: "Recordatorio", color: "#B88537" },
  { value: "otro", label: "Otro", color: "#4A6D8C" },
];

// Empty state for reminder filters (uses "tipo" from calendar event schema)
const RECORDATORIO_FILTROS = [
  { value: "todos", label: "Todos" },
  { value: "reunion", label: "Reuniones" },
  { value: "cobro", label: "Vencimientos" },
  { value: "proceso", label: "Procesos" },
  { value: "notarial", label: "Notariales" },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}

function displayName(user) {
  let n = (user?.name || "").trim();
  if (!n) {
    // Fallback al email: usa la parte antes del @, capitalizada
    const raw = (user?.email || "").split("@")[0].replace(/[._-]+/g, " ").trim();
    n = raw.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (!n) return "Licenciado";
  // Elimina prefijos de título repetidos ("Lic.", "Licda.", "Dr.", "Dra.", "Msc.")
  n = n.replace(/^(?:lic(?:da)?\.?|dr(?:a)?\.?|msc\.?)\s+/gi, "").trim();
  // Devuelve nombre + primer apellido si hay al menos 2 palabras
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]} ${parts[1]}`;
  return parts[0] || "Licenciado";
}

function buildMonthMatrix(year, month) {
  // Monday-first weeks. Returns array of weeks, each week is array of 7 Date objects.
  const first = new Date(year, month, 1);
  const dayOfWeek = (first.getDay() + 6) % 7; // 0=Mon
  const start = new Date(year, month, 1 - dayOfWeek);
  const weeks = [];
  const cur = new Date(start);
  for (let w = 0; w < 6; w++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      row.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    weeks.push(row);
  }
  // Trim trailing week if entirely outside month
  while (weeks.length > 4) {
    const last = weeks[weeks.length - 1];
    if (last.every((d) => d.getMonth() !== month)) weeks.pop();
    else break;
  }
  return weeks;
}

function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatFechaLarga(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("es-CR", { weekday: "long", day: "numeric", month: "long" });
}

// ============================================================================
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = new Date();

  const [cursor, setCursor] = useState(() => ({ y: today.getFullYear(), m: today.getMonth() }));
  const [events, setEvents] = useState([]);
  const [recordatorios, setRecordatorios] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loadingCal, setLoadingCal] = useState(true);
  const [loadingRec, setLoadingRec] = useState(true);
  const [loadingSol, setLoadingSol] = useState(true);

  const [filtroRec, setFiltroRec] = useState("todos");
  const [dayDialog, setDayDialog] = useState({ open: false, fecha: "" });
  const [deleteEv, setDeleteEv] = useState(null);

  const loadCalendar = () => {
    setLoadingCal(true);
    api.get(`/dashboard/calendar?year=${cursor.y}&month=${cursor.m + 1}`)
      .then((r) => setEvents(r.data))
      .catch(() => toast.error("Error cargando calendario"))
      .finally(() => setLoadingCal(false));
  };

  const loadRecordatorios = () => {
    setLoadingRec(true);
    api.get("/dashboard/recordatorios")
      .then((r) => setRecordatorios(r.data))
      .catch(() => {})
      .finally(() => setLoadingRec(false));
  };

  const loadSolicitudes = () => {
    setLoadingSol(true);
    api.get("/solicitudes?estado=pendiente")
      .then((r) => setSolicitudes(r.data))
      .catch(() => {})
      .finally(() => setLoadingSol(false));
  };

  useEffect(() => { loadCalendar(); /* eslint-disable-line */ }, [cursor.y, cursor.m]);
  useEffect(() => { loadRecordatorios(); loadSolicitudes(); }, []);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const k = ev.fecha;
      (map[k] ||= []).push(ev);
    }
    return map;
  }, [events]);

  const monthMatrix = useMemo(() => buildMonthMatrix(cursor.y, cursor.m), [cursor]);
  const diasConEventos = Object.keys(eventsByDay).length;

  const goPrev = () => setCursor((c) => {
    const m = c.m - 1;
    return m < 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m };
  });
  const goNext = () => setCursor((c) => {
    const m = c.m + 1;
    return m > 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m };
  });
  const goToday = () => setCursor({ y: today.getFullYear(), m: today.getMonth() });

  const filteredRecordatorios = useMemo(() => {
    if (filtroRec === "todos") return recordatorios;
    return recordatorios.filter((r) => r.tipo === filtroRec);
  }, [recordatorios, filtroRec]);

  const onDeleteEvent = async () => {
    if (!deleteEv) return;
    try {
      await api.delete(`/dashboard/eventos/${deleteEv.id}`);
      toast.success("Evento eliminado");
      setDeleteEv(null);
      loadCalendar();
      loadRecordatorios();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Greeting */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Panel principal</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl text-[#1A0810]" data-testid="dashboard-greeting">
            {getGreeting()}, <span className="text-[#7A1E2E]">Lic. {displayName(user)}</span>.
          </h1>
          <p className="mt-1 text-sm text-[#4a3838]">
            Este es el estado del despacho al día de hoy.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 rounded-md bg-white border border-[#E5DFD3] px-4 py-2 text-sm text-[#1A0810]">
          <CalendarIcon className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.6} />
          {today.toLocaleDateString("es-CR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </div>
      </div>

      {/* Calendar */}
      <div className="rounded-xl bg-white border border-[#E5DFD3] p-6" data-testid="dashboard-calendar">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center">
              <CalendarIcon className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.6} />
            </div>
            <div>
              <div className="font-display text-2xl text-[#1A0810]" data-testid="calendar-title">
                {MONTH_NAMES[cursor.m]} de {cursor.y}
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">
                {diasConEventos} día{diasConEventos === 1 ? "" : "s"} con eventos
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={goPrev}
              data-testid="calendar-prev"
              className="h-9 w-9 rounded-md border border-[#E5DFD3] bg-white hover:bg-[#F5EEE0] text-[#7A1E2E] flex items-center justify-center"
              title="Mes anterior"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
            </button>
            <button
              onClick={goToday}
              data-testid="calendar-today"
              className="h-9 px-4 rounded-md border border-[#E5DFD3] bg-white hover:bg-[#F5EEE0] text-[#1A0810] text-sm font-medium"
            >
              Hoy
            </button>
            <button
              onClick={goNext}
              data-testid="calendar-next"
              className="h-9 w-9 rounded-md border border-[#E5DFD3] bg-white hover:bg-[#F5EEE0] text-[#7A1E2E] flex items-center justify-center"
              title="Mes siguiente"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-[10px] uppercase tracking-[0.22em] text-[#6B5D57] font-semibold text-center">
              {w}
            </div>
          ))}
        </div>

        {/* Grid */}
        {loadingCal ? (
          <div className="p-16 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#7A1E2E] mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-2">
            {monthMatrix.flat().map((d, idx) => {
              const isCurMonth = d.getMonth() === cursor.m;
              const key = ymd(d);
              const isToday = key === ymd(today);
              const dayEvents = eventsByDay[key] || [];
              return (
                <button
                  key={idx}
                  onClick={() => setDayDialog({ open: true, fecha: key })}
                  data-testid={`calendar-day-${key}`}
                  className={`text-left min-h-[110px] rounded-lg border p-2 transition-all
                    ${isCurMonth ? "bg-white border-[#E5DFD3] hover:border-[#7A1E2E]/50 hover:shadow-sm" : "bg-[#FBF9F4]/40 border-transparent text-[#B8AEA5]"}
                    ${isToday ? "!border-[#7A1E2E] ring-2 ring-[#7A1E2E]/20" : ""}`}
                >
                  <div className={`text-[13px] font-semibold ${isToday ? "text-[#7A1E2E]" : isCurMonth ? "text-[#1A0810]" : "text-[#B8AEA5]"}`}>
                    {d.getDate()}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {dayEvents.slice(0, 3).map((ev, i) => (
                      <div
                        key={i}
                        className="text-[10px] leading-tight rounded px-1.5 py-0.5 truncate"
                        style={{ backgroundColor: `${ev.color}15`, color: ev.color, borderLeft: `2px solid ${ev.color}` }}
                        title={ev.titulo}
                      >
                        <span className="font-semibold">•</span> {ev.titulo}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-[#6B5D57] font-medium">+{dayEvents.length - 3} más</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Recordatorios + Solicitudes pendientes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recordatorios */}
        <div className="rounded-xl bg-white border border-[#E5DFD3] p-6" data-testid="dashboard-recordatorios">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center">
              <Bell className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <div className="font-display text-xl text-[#1A0810]">Recordatorios</div>
              <div className="text-xs text-[#6B5D57]">Eventos en curso y próximos, ordenados por fecha</div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-1.5">
            {RECORDATORIO_FILTROS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFiltroRec(f.value)}
                data-testid={`recordatorio-filter-${f.value}`}
                className={`h-7 px-3 rounded-full text-[11px] font-semibold uppercase tracking-wide transition
                  ${filtroRec === f.value ? "bg-[#2B0913] text-[#F1EDE4]" : "bg-[#F5EEE0] text-[#4a3838] hover:bg-[#EEE4D2]"}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="mt-4 space-y-2 max-h-[380px] overflow-y-auto pr-1">
            {loadingRec ? (
              <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#7A1E2E] mx-auto" /></div>
            ) : filteredRecordatorios.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#6B5D57]">Sin recordatorios en el horizonte</div>
            ) : (
              filteredRecordatorios.map((r) => {
                const fechaHoy = ymd(today);
                const esHoy = r.fecha === fechaHoy;
                const enCurso = esHoy;
                return (
                  <div
                    key={`${r.tipo}-${r.id}`}
                    className="group flex items-start gap-3 rounded-lg border border-[#E5DFD3] bg-[#FBF9F4] px-3 py-2.5 hover:border-[#7A1E2E]/40 transition"
                  >
                    <div
                      className="mt-0.5 h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${r.color}20` }}
                    >
                      <Bell className="h-4 w-4" strokeWidth={1.8} style={{ color: r.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1A0810] font-medium truncate">{r.titulo}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-[#6B5D57]">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" strokeWidth={1.8} />
                          {formatFechaLarga(r.fecha)}{r.hora ? ` · ${r.hora}` : ""}
                        </span>
                        {r.detalle && <span className="truncate">{r.detalle}</span>}
                      </div>
                    </div>
                    {enCurso && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[#C25A5A]">En curso</span>
                    )}
                    {r.editable && (
                      <button
                        onClick={() => setDeleteEv(r)}
                        data-testid={`recordatorio-delete-${r.id}`}
                        className="opacity-0 group-hover:opacity-100 h-7 w-7 rounded-md hover:bg-[#FBEDEF] flex items-center justify-center text-[#7A1E2E]"
                        title="Eliminar"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Solicitudes pendientes */}
        <div className="rounded-xl bg-white border border-[#E5DFD3] p-6" data-testid="dashboard-solicitudes">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center">
              <ListChecks className="h-5 w-5 text-[#B88537]" strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <div className="font-display text-xl text-[#1A0810]">Solicitudes pendientes</div>
              <div className="text-xs text-[#6B5D57]">
                {loadingSol ? "..." : `${solicitudes.length} tarea${solicitudes.length === 1 ? "" : "s"} por hacer`}
              </div>
            </div>
            <button
              onClick={() => navigate("/solicitudes")}
              data-testid="ver-todas-solicitudes"
              className="inline-flex items-center gap-1 text-xs text-[#7A1E2E] hover:text-[#4A0E1C] font-medium"
            >
              Ver todas <ExternalLink className="h-3 w-3" strokeWidth={1.8} />
            </button>
          </div>

          <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {loadingSol ? (
              <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#7A1E2E] mx-auto" /></div>
            ) : solicitudes.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#6B5D57]">Sin solicitudes pendientes</div>
            ) : (
              solicitudes.map((s) => (
                <button
                  key={s.id}
                  onClick={() => navigate("/solicitudes")}
                  data-testid={`solicitud-item-${s.id}`}
                  className="w-full text-left flex items-center gap-3 rounded-lg border border-[#E5DFD3] bg-white px-3 py-2.5 hover:border-[#B88537]/60 hover:bg-[#FBF9F4] transition"
                >
                  <div className="h-5 w-5 rounded-full border-2 border-[#C19B6C] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#1A0810] font-medium truncate">{s.titulo}</div>
                    {(s.descripcion || s.subtipo) && (
                      <div className="text-xs text-[#6B5D57] truncate">{s.descripcion || s.subtipo}</div>
                    )}
                  </div>
                  {s.fecha_limite && (
                    <span className="text-[11px] text-[#6B5D57] whitespace-nowrap">
                      {new Date(s.fecha_limite + "T12:00:00").toLocaleDateString("es-CR", { day: "2-digit", month: "short" })}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Day dialog */}
      <DayDialog
        state={dayDialog}
        events={dayDialog.open ? (eventsByDay[dayDialog.fecha] || []) : []}
        onOpenChange={(o) => setDayDialog((s) => ({ ...s, open: o }))}
        onSaved={() => {
          loadCalendar();
          loadRecordatorios();
        }}
        onDelete={(ev) => setDeleteEv(ev)}
      />

      {/* Confirm delete */}
      <AlertDialog open={!!deleteEv} onOpenChange={(o) => !o && setDeleteEv(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810]">Eliminar evento</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              ¿Deseas eliminar <b>{deleteEv?.titulo}</b> del calendario? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteEvent}
              data-testid="confirm-delete-evento"
              className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
function DayDialog({ state, events, onOpenChange, onSaved, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ titulo: "", hora: "", tipo: "reunion", descripcion: "", color: "#7A1E2E" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (state.open) {
      setShowForm(false);
      setForm({ titulo: "", hora: "", tipo: "reunion", descripcion: "", color: "#7A1E2E" });
    }
  }, [state.open, state.fecha]);

  const save = async () => {
    if (!form.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      await api.post("/dashboard/eventos", {
        fecha: state.fecha,
        hora: form.hora,
        titulo: form.titulo.trim(),
        descripcion: form.descripcion,
        tipo: form.tipo,
        color: EVENT_TIPOS.find((t) => t.value === form.tipo)?.color || "#7A1E2E",
      });
      toast.success("Evento agregado");
      onSaved?.();
      setShowForm(false);
      setForm({ titulo: "", hora: "", tipo: "reunion", descripcion: "", color: "#7A1E2E" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={state.open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#FBF9F4] border-[#E5DFD3]" data-testid="day-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A0810] capitalize">
            {formatFechaLarga(state.fecha)}
          </DialogTitle>
          <DialogDescription className="text-[#6B5D57]">
            {events.length === 0 ? "Sin eventos registrados este día." : `${events.length} evento${events.length === 1 ? "" : "s"} en la agenda.`}
          </DialogDescription>
        </DialogHeader>

        {events.length > 0 && (
          <div className="space-y-2 max-h-[220px] overflow-y-auto">
            {events.map((ev, i) => (
              <div
                key={`${ev.tipo}-${ev.ref_id || i}`}
                className="group flex items-start gap-2 rounded-md bg-white border border-[#E5DFD3] px-3 py-2"
                style={{ borderLeft: `3px solid ${ev.color}` }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1A0810] font-medium truncate">{ev.titulo}</div>
                  <div className="text-[11px] text-[#6B5D57] flex items-center gap-2">
                    <span className="uppercase tracking-wider font-semibold">{ev.tipo.replace("_", " ")}</span>
                    {ev.hora && <span>· {ev.hora}</span>}
                  </div>
                  {ev.descripcion && (
                    <div className="text-xs text-[#4a3838] mt-1">{ev.descripcion}</div>
                  )}
                </div>
                {ev.editable && (
                  <button
                    onClick={() => onDelete?.(ev)}
                    data-testid={`day-delete-${ev.id}`}
                    className="opacity-0 group-hover:opacity-100 h-7 w-7 rounded-md hover:bg-[#FBEDEF] flex items-center justify-center text-[#7A1E2E]"
                    title="Eliminar"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {!showForm ? (
          <Button
            onClick={() => setShowForm(true)}
            data-testid="day-add-button"
            className="w-full bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Agregar reunión / recordatorio
          </Button>
        ) : (
          <div className="space-y-3 rounded-lg border border-[#E5DFD3] bg-white p-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Título *</Label>
              <Input
                data-testid="day-form-titulo"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="Reunión con cliente..."
                className="h-11 bg-white border-[#E5DFD3] mt-2 focus-visible:ring-[#7A1E2E]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Hora</Label>
                <Input
                  data-testid="day-form-hora"
                  type="time"
                  value={form.hora}
                  onChange={(e) => setForm({ ...form, hora: e.target.value })}
                  className="h-11 bg-white border-[#E5DFD3] mt-2 focus-visible:ring-[#7A1E2E]"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="day-form-tipo" className="h-11 bg-white border-[#E5DFD3] mt-2 focus-visible:ring-[#7A1E2E]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Notas</Label>
              <Textarea
                data-testid="day-form-descripcion"
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                placeholder="Detalles opcionales"
                className="mt-2 min-h-[70px] bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {showForm ? (
            <>
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">
                Cancelar
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid="day-form-save"
                className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar evento"}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
